import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Calculator, ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import { Input } from "../../components/ui/input.js";
import { Button } from "../../components/ui/button.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Badge } from "../../components/ui/badge.js";
import { Label } from "../../components/ui/label.js";
import { Textarea } from "../../components/ui/textarea.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs.js";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog.js";
import { cn } from "../../lib/utils.js";
import {
  COMP_LABELS, COMP_STATUS_BADGE, EARN_KEYS, fmtAmt, fmtDate, takeHomeBeforeTax, type CompStatus,
} from "../../lib/compensation.js";

// ── Every rule value the breakdown engine uses (mirrors the API's StructureConfig) ──
// One map drives the formulas, the change highlighting, Finance's diff and the history.
const META = {
  basicRate:         { label: "Basic %",                           kind: "pct" },
  lowCtcCeiling:     { label: "Low-CTC limit",                     kind: "rupee" },
  lowCtcOther:       { label: "Low-CTC Other Allowances",          kind: "rupee" },
  hraNilCeiling:     { label: "HRA nil up to",                     kind: "rupee" },
  hraLowCtcCeiling:  { label: "HRA low rate up to CTC",            kind: "rupee" },
  hraLowRate:        { label: "HRA low rate",                      kind: "pct" },
  hraHighRate:       { label: "HRA high rate",                     kind: "pct" },
  ltaCeiling:        { label: "LTA nil up to",                     kind: "rupee" },
  ltaRate:           { label: "LTA rate",                          kind: "pct" },
  telCeiling:        { label: "Telephone nil up to",               kind: "rupee" },
  telAmount:         { label: "Telephone amount",                  kind: "rupee" },
  eduCeiling:        { label: "Education nil up to",               kind: "rupee" },
  eduRate:           { label: "Education rate",                    kind: "pct" },
  eduCap:            { label: "Education max",                     kind: "rupee" },
  hostelCeiling:     { label: "Hostel nil up to",                  kind: "rupee" },
  hostelRate:        { label: "Hostel rate",                       kind: "pct" },
  hostelCap:         { label: "Hostel max",                        kind: "rupee" },
  vehicleFloor:      { label: "Vehicle from",                      kind: "rupee" },
  vehicleRate:       { label: "Vehicle rate",                      kind: "pct" },
  esiCtcCeiling:     { label: "ESI below Fixed CTC",               kind: "rupee" },
  esiFactor:         { label: "ESI wage factor",                   kind: "pct" },
  esiEmployeeRate:   { label: "ESI (Employee) rate",               kind: "pct" },
  esiEmployerRate:   { label: "ESI (Employer) rate",               kind: "pct" },
  pfWageCeilingMonthly: { label: "PF wage ceiling (₹/month)",      kind: "rupee" },
  pfEmployeeRate:    { label: "PF (Employee) rate",                kind: "pct" },
  pfEmployerRate:    { label: "PF (Employer) rate",                kind: "pct" },
  gratuityRate:      { label: "Gratuity rate",                     kind: "pct" },
} as const;
type Key = keyof typeof META;
type StructureConfig = Record<Key, number>;
const KEYS = Object.keys(META) as Key[];

interface StructVersion {
  id: string; version: number; config: StructureConfig; status: CompStatus; note: string | null;
  submittedBy: string; submittedAt: string; decidedBy: string | null; decidedAt: string | null; decisionComment: string | null;
  /** Approving restructures every employee's approved compensation from this date. */
  appliesFrom: string | null;
  applySummary: {
    appliesFrom: string; restructured: number; recalculated: number; unchanged: number;
    skipped: { employeeId: string; name: string; reason: string }[];
  } | null;
}

/** Default "applies from": the 1st of next month, as YYYY-MM-DD. */
function firstOfNextMonth(): string {
  const n = new Date();
  const d = new Date(n.getFullYear(), n.getMonth() + 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
interface Overview {
  active: { version: number | null; config: StructureConfig } & Partial<StructVersion>;
  pending: StructVersion | null;
  history: StructVersion[];
  userNames: Record<string, string>;
  canPropose: boolean; canVerify: boolean; canWithdraw: boolean;
}

const toPct  = (frac: number) => String(+(frac * 100).toFixed(4));
const show   = (k: Key, v: number) => (META[k].kind === "pct" ? `${toPct(v)}%` : `₹${fmtAmt(v)}`);
/** Draft (as typed) → number in engine units, or null when not a valid number. */
function parse(k: Key, s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  if (META[k].kind === "pct") return n > 100 ? null : +(n / 100).toFixed(6);
  return n;
}
const draftOf = (c: StructureConfig) =>
  Object.fromEntries(KEYS.map(k => [k, META[k].kind === "pct" ? toPct(c[k]) : String(c[k])])) as Record<Key, string>;
const changedKeys = (from: StructureConfig, to: StructureConfig) => KEYS.filter(k => from[k] !== to[k]);

// ── Formula rows ─────────────────────────────────────────────────────────────

/** Open/close control for a card header (also used by the other Compensation Rules cards). */
export function CollapseToggle({ open, onToggle, label }: { open: boolean; onToggle: () => void; label: string }) {
  return (
    <Button type="button" size="sm" variant="ghost" className="h-7 w-7 shrink-0 p-0" aria-expanded={open}
      aria-label={`${open ? "Collapse" : "Expand"} ${label}`} onClick={onToggle}>
      {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
    </Button>
  );
}

/** One line of "how we calculate": component name, then the formula with its values inline. */
function RuleRow({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-dashed border-surface-100 py-2.5 last:border-b-0 sm:grid-cols-[170px_1fr] sm:gap-4">
      <span className="text-[13px] font-semibold text-surface-800">{title}</span>
      <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] leading-7 text-surface-600">{children}</span>
    </div>
  );
}

/** `verifyOnly` (Payroll > Structure Verification): only the proposal waiting for Finance and
 *  the change history — no editing, no formulas. */
export default function CompStructurePanel({ verifyOnly = false }: { verifyOnly?: boolean } = {}) {
  const toast = useToast();
  const [data, setData] = useState<Overview | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<Key, string> | null>(null);
  const [note, setNote] = useState("");
  const [appliesFrom, setAppliesFrom] = useState(firstOfNextMonth);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [decisionComment, setDecisionComment] = useState("");
  const [decisionErr, setDecisionErr] = useState<string | null>(null);
  // Read-only on load; the Super HR Admin clicks Edit to change values.
  const [editing, setEditing] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(true);
  const [structOpen, setStructOpen]   = useState(true);
  // In Finance's All requests tab the history is the content, so it starts open there.
  const [historyOpen, setHistoryOpen] = useState(verifyOnly);
  const [vTab, setVTab] = useState<"pending" | "all">("pending");

  const load = useCallback(async () => {
    try {
      const r = await api.get<Overview>("/compensation/structure");
      setData(r.data); setDraft(draftOf(r.data.active.config)); setLoadErr(null);
    } catch (e) {
      logError("CompStructurePanel.load", e);
      setLoadErr(errMsg(e) ?? "Couldn't load the salary structure rules.");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const active = data?.active.config;
  // The draft as numbers; null if any value is invalid.
  const draftConfig = useMemo<StructureConfig | null>(() => {
    if (!draft) return null;
    const out = {} as StructureConfig;
    for (const k of KEYS) { const v = parse(k, draft[k]); if (v === null) return null; out[k] = v; }
    return out;
  }, [draft]);
  const invalid = !!draft && !draftConfig;
  const changes = active && draftConfig ? changedKeys(active, draftConfig) : [];
  const canEdit  = !verifyOnly && !!data?.canPropose && !data.pending;
  // Finance's view shows the proposed rules in the formulas, each change marked against the rules in use.
  const proposed = verifyOnly ? data?.pending?.config ?? null : null;
  const lowCtcOff = (proposed ?? data?.active.config)?.lowCtcCeiling === 0;
  const editable = canEdit && editing;
  const stopEditing = () => { if (active) setDraft(draftOf(active)); setNote(""); setAppliesFrom(firstOfNextMonth()); setSubmitErr(null); setEditing(false); };

  /** A value inside a formula: an input for the Super HR Admin, plain text otherwise. */
  const V = (k: Key) => {
    if (!draft || !active) return null;
    const pct = META[k].kind === "pct";
    if (proposed && proposed[k] !== active[k]) {
      return (
        <span className="inline-flex items-center gap-1">
          <span className="rounded border border-[var(--color-warning-border)] bg-[var(--color-warning-bg-subtle)] px-1.5 font-semibold tabular-nums text-surface-900">
            {show(k, proposed[k])}
          </span>
          <span className="text-[11px] text-surface-400">(was {show(k, active[k])})</span>
        </span>
      );
    }
    if (!editable) return <span className="font-semibold tabular-nums text-surface-900">{show(k, active[k])}</span>;
    const v = parse(k, draft[k]);
    const changed = v !== null && v !== active[k];
    return (
      <span className="inline-flex items-center gap-1">
        {!pct && <span className="text-surface-500">₹</span>}
        <Input
          type="number" min={0} step="any" aria-label={META[k].label}
          value={draft[k]}
          onChange={e => setDraft(d => (d ? { ...d, [k]: e.target.value } : d))}
          className={cn("h-7 text-right tabular-nums", pct ? "w-20" : "w-28",
            v === null && "border-[var(--color-error-border)]",
            changed && "border-[var(--color-warning-border)] bg-[var(--color-warning-bg-subtle)]")}
        />
        {pct && <span className="text-surface-500">%</span>}
        {changed && <span className="text-[11px] text-surface-400">(was {show(k, active[k])})</span>}
      </span>
    );
  };

  async function submit() {
    setSubmitErr(null);
    if (!draftConfig) { setSubmitErr("Fix the highlighted values first."); throw new Error("validation"); }
    if (changes.length === 0) { setSubmitErr("Change at least one value."); throw new Error("validation"); }
    if (!note.trim()) { setSubmitErr("Say what changed and why — Finance sees this note."); throw new Error("validation"); }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(appliesFrom)) { setSubmitErr("Pick the date the new rules apply to all employees from."); throw new Error("validation"); }
    try {
      await api.post("/compensation/structure", { config: draftConfig, note: note.trim(), appliesFrom });
      toast.show(`Sent to Finance. Once approved, every employee's salary is restructured from ${fmtDate(appliesFrom)}.`, "success");
      setNote(""); setEditing(false); await load();
    } catch (e) {
      logError("CompStructurePanel.submit", e);
      setSubmitErr(errMsg(e) ?? "Couldn't send the change to Finance."); throw e;
    }
  }

  async function decideOn(action: "approve" | "reject" | "withdraw") {
    if (!data?.pending) return;
    setDecisionErr(null);
    if (action === "reject" && !decisionComment.trim()) { setDecisionErr("Add a comment saying why it's rejected."); throw new Error("validation"); }
    try {
      await api.post(`/compensation/structure/${data.pending.id}/${action}`,
        action === "withdraw" ? {} : { comment: decisionComment.trim() || undefined });
      setDecisionComment(""); await load();
    } catch (e) {
      logError(`CompStructurePanel.${action}`, e);
      setDecisionErr(errMsg(e) ?? "Couldn't update this version."); throw e;
    }
  }

  if (loadErr) return <p className="m-0 text-[13px] text-[var(--color-error-fg)]">{loadErr}</p>;
  if (!data || !draft || !active) return <p className="m-0 text-[13px] text-surface-400">Loading salary structure…</p>;
  const name = (id: string | null) => (id ? data.userNames[id] ?? "Unknown user" : "—");
  const pending = data.pending;
  // Finance's view splits into Pending (the proposal to decide) and All requests (every version).
  const showPending = !verifyOnly || vTab === "pending";
  const showHistory = !verifyOnly || vTab === "all";

  return (
    <div className="flex flex-col gap-6">
      {verifyOnly && (
        <Tabs value={vTab} onValueChange={v => setVTab(v as "pending" | "all")}>
          <TabsList>
            <TabsTrigger value="pending">Pending ({pending ? 1 : 0})</TabsTrigger>
            <TabsTrigger value="all">All requests ({data.history.length})</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* ── A proposal waiting for Finance ── */}
      {showPending && pending && active && (
        <Card className="border-[var(--color-warning-border)]">
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="cursor-pointer" onClick={() => setPendingOpen(o => !o)}>Version {pending.version} — waiting for Finance</CardTitle>
              <Badge tone="warning">Unconfirmed</Badge>
              <span className="ml-auto"><CollapseToggle open={pendingOpen} onToggle={() => setPendingOpen(o => !o)} label="pending version" /></span>
            </div>
            <CardDescription>
              Proposed by {name(pending.submittedBy)} on {fmtDate(pending.submittedAt)}. Until Finance approves it, calculations keep using
              version {data.active.version ?? "—"}.
              {pending.appliesFrom
                ? <> On approval, <strong>every employee's approved salary is recalculated</strong> with these rules from <strong>{fmtDate(pending.appliesFrom)}</strong> (a new Restructuring revision each); open revisions are recalculated too.</>
                : " This proposal has no 'applies from' date, so approving it changes new revisions only."}
            </CardDescription>
          </CardHeader>
          {pendingOpen && <CardContent className="flex flex-col gap-3">
            {pending.note && <p className="m-0 rounded-md bg-surface-50 px-3 py-2 text-[12px] text-surface-700">“{pending.note}”</p>}
            <div className="overflow-x-auto rounded-md border border-surface-200">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Rule</TableHead><TableHead className="text-right">In use now</TableHead><TableHead className="text-right">Proposed</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {changedKeys(active, pending.config).map(k => (
                    <TableRow key={k}>
                      <TableCell className="px-3">{META[k].label}</TableCell>
                      <TableCell className="px-3 text-right tabular-nums">{show(k, active[k])}</TableCell>
                      <TableCell className="px-3 text-right font-semibold tabular-nums">{show(k, pending.config[k])}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {data.canVerify && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="struct-decision">Finance comment</Label>
                <Textarea id="struct-decision" rows={2} placeholder="Required when rejecting; optional when approving"
                  value={decisionComment} onChange={e => setDecisionComment(e.target.value)} />
              </div>
            )}
            {!data.canVerify && data.canPropose && (
              <p className="m-0 text-[12px] text-surface-500">Someone in Finance (other than the person who proposed it) has to verify this.</p>
            )}
            {decisionErr && <p className="m-0 text-[12px] text-[var(--color-error-fg)]">{decisionErr}</p>}
            <div className="flex flex-wrap gap-2">
              {data.canVerify && (
                <>
                  <AsyncButton variant="success" onClick={() => decideOn("approve")}
                    confirm={{
                      title: `Approve rules version ${pending.version}?`,
                      description: pending.appliesFrom
                        ? `Every employee's approved salary will be recalculated with these rules from ${fmtDate(pending.appliesFrom)} — each gets a new approved Restructuring revision (earlier revisions stay as history). Open revisions are recalculated. This cannot be undone automatically.`
                        : "New revisions and previews will be calculated with these rules from now on. Verified compensation is not changed.",
                      actionText: pending.appliesFrom ? "Approve and apply to all" : "Approve",
                    }}>
                    Approve rules
                  </AsyncButton>
                  <AsyncButton variant="destructive" onClick={() => decideOn("reject")}>Reject</AsyncButton>
                </>
              )}
              {data.canWithdraw && (
                <AsyncButton variant="outline" animated={false} onClick={() => (window.confirm("Withdraw this proposal?") ? decideOn("withdraw") : undefined)}>
                  Withdraw proposal
                </AsyncButton>
              )}
            </div>
          </CardContent>}
        </Card>
      )}

      {verifyOnly && showPending && !pending && (
        <Card>
          <CardContent className="py-6 text-center text-[13px] text-surface-400">
            No compensation structure changes are waiting for verification. Version {data.active.version ?? "—"} is in use.
          </CardContent>
        </Card>
      )}

      {/* ── The rules in use, as formulas ── */}
      {showPending && <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="cursor-pointer" onClick={() => setStructOpen(o => !o)}>Compensation structure — how the breakdown is calculated</CardTitle>
            <Badge tone="success">Version {data.active.version ?? "—"} in use</Badge>
            {proposed && pending && <Badge tone="warning">Showing version {pending.version} as proposed — changes highlighted</Badge>}
            {!verifyOnly && !data.canPropose && <Badge tone="outline">Read only: Super HR Admin can edit</Badge>}
            {!verifyOnly && data.canPropose && pending && <Badge tone="outline">Editing is locked while version {pending.version} is with Finance</Badge>}
            <span className="ml-auto flex items-center gap-1">
              <StructurePreview
                active={active}
                draft={proposed ?? (editable && changes.length > 0 ? draftConfig : null)}
                draftLabel={proposed ? "Proposed" : "Your changes"}
              />
              {canEdit && !editing && (
                <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => { setEditing(true); setStructOpen(true); }}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              )}
              <CollapseToggle open={structOpen} onToggle={() => setStructOpen(o => !o)} label="salary structure" />
            </span>
          </div>
          <CardDescription>
            Fixed CTC = CTC − Bonus. The bonus is paid yearly and is never part of monthly salary or take-home.
            {data.active.decidedAt && ` Approved by ${name(data.active.decidedBy ?? null)} on ${fmtDate(data.active.decidedAt)}.`}
          </CardDescription>
        </CardHeader>
        {structOpen && <CardContent>
          {/* One structure for every employee. */}
          <Tabs defaultValue="standard">
            <TabsList>
              <TabsTrigger value="standard">Standard</TabsTrigger>
              <TabsTrigger value="statutory">Statutory</TabsTrigger>
            </TabsList>

            <TabsContent value="standard" className="pt-3">
              <RuleRow title="Basic Salary">Fixed CTC × {V("basicRate")}</RuleRow>
              <RuleRow title="HRA">0 if Fixed CTC ≤ {V("hraNilCeiling")}; Basic × {V("hraLowRate")} if CTC ≤ {V("hraLowCtcCeiling")}; otherwise Basic × {V("hraHighRate")}</RuleRow>
              {/* These five test Fixed CTC − Bonus (the workbook subtracts the bonus again), as the engine does. */}
              <RuleRow title="LTA">0 if Fixed CTC − Bonus ≤ {V("ltaCeiling")}; otherwise Basic × {V("ltaRate")}</RuleRow>
              <RuleRow title="Telephone">0 if Fixed CTC − Bonus ≤ {V("telCeiling")}; otherwise {V("telAmount")} a year</RuleRow>
              <RuleRow title="Education">0 if Fixed CTC − Bonus ≤ {V("eduCeiling")}; otherwise Basic × {V("eduRate")}, at most {V("eduCap")}</RuleRow>
              <RuleRow title="Hostel">0 if Fixed CTC − Bonus ≤ {V("hostelCeiling")}; otherwise Basic × {V("hostelRate")}, at most {V("hostelCap")}</RuleRow>
              <RuleRow title="Vehicle">Basic × {V("vehicleRate")} if Fixed CTC − Bonus ≥ {V("vehicleFloor")}; otherwise 0</RuleRow>
              {/* The low-CTC fixed amount is switched off by a limit of 0 (the 2026 New 3 workbook drops it). */}
              <RuleRow title="Other Allowances">
                Fixed CTC − Basic − all allowances above
                {lowCtcOff && !editable
                  ? <span className="text-surface-400">(low-CTC fixed amount is off)</span>
                  : <>; except {V("lowCtcOther")} if Fixed CTC &lt; {V("lowCtcCeiling")} <span className="text-[11px] text-surface-400">(limit 0 = off)</span></>}
              </RuleRow>
              <RuleRow title="Gross">Basic + all allowances above (= Fixed CTC). PF, ESI and Gratuity are on the Statutory tab.</RuleRow>
            </TabsContent>

            {/* Every value that drives a statutory line, on the line it drives. */}
            <TabsContent value="statutory" className="pt-3">
              <RuleRow title="PF wage">Monthly Basic, at most {V("pfWageCeilingMonthly")} a month</RuleRow>
              <RuleRow title="PF (Employee)">PF wage × {V("pfEmployeeRate")} × 12</RuleRow>
              <RuleRow title="PF (Employer)">PF wage × {V("pfEmployerRate")} × 12 (incl. admin/EDLI)</RuleRow>
              <RuleRow title="Gratuity">Basic × {V("gratuityRate")}</RuleRow>
              <RuleRow title="ESI applies">Only if Fixed CTC &lt; {V("esiCtcCeiling")}; otherwise ESI is 0</RuleRow>
              <RuleRow title="ESI wage">(Gross − Gratuity − Employer PF) × {V("esiFactor")}</RuleRow>
              <RuleRow title="ESI (Employee)">ESI wage × {V("esiEmployeeRate")}</RuleRow>
              <RuleRow title="ESI (Employer)">ESI wage × {V("esiEmployerRate")}</RuleRow>
              <RuleRow title="Take home">Gross − Gratuity − PF (employee + employer) − ESI − Medical Insurance</RuleRow>
              <RuleRow title="Income tax">Slabs, rebate and cess are in the Income tax card below.</RuleRow>
            </TabsContent>
          </Tabs>

          {editable && (
            <div className="mt-4 flex flex-col gap-2 border-t border-surface-100 pt-4">
              <Label htmlFor="struct-applies">Apply to all employees from</Label>
              <Input id="struct-applies" type="date" className="h-8 w-44" value={appliesFrom} onChange={e => setAppliesFrom(e.target.value)} />
              <p className="m-0 text-[12px] text-surface-500">
                When Finance approves, every employee's approved salary is recalculated with these rules from this date (a new Restructuring revision each, same CTC).
              </p>
              <Label htmlFor="struct-note">What changed and why (Finance sees this)</Label>
              <Textarea id="struct-note" rows={2} value={note} onChange={e => setNote(e.target.value)}
                placeholder="e.g. Basic reduced to 45% from FY 2027 per the new salary policy" />
              {submitErr && <p className="m-0 text-[12px] text-[var(--color-error-fg)]">{submitErr}</p>}
              <div className="flex flex-wrap items-center justify-end gap-3">
                <span className="text-[12px] text-surface-500">
                  {invalid ? "Some values are invalid" : changes.length === 0 ? "No changes yet" : `${changes.length} value${changes.length > 1 ? "s" : ""} changed`}
                </span>
                <Button variant="secondary" onClick={stopEditing}>Cancel</Button>
                <AsyncButton disabled={changes.length === 0 || invalid} onClick={() => submit()}>Send to Finance</AsyncButton>
              </div>
            </div>
          )}
        </CardContent>}
      </Card>}

      {/* ── Every version, newest first ── */}
      {showHistory && <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="cursor-pointer" onClick={() => setHistoryOpen(o => !o)}>Change history</CardTitle>
            <Badge tone="neutral">{data.history.length} version{data.history.length === 1 ? "" : "s"}</Badge>
            <span className="ml-auto"><CollapseToggle open={historyOpen} onToggle={() => setHistoryOpen(o => !o)} label="change history" /></span>
          </div>
          <CardDescription>Every version is kept, with who proposed it, who verified it and exactly what changed.</CardDescription>
        </CardHeader>
        {historyOpen && <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead><TableHead>Status</TableHead><TableHead>Proposed</TableHead>
                <TableHead>Decided</TableHead><TableHead>What changed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.history.map((v, i) => {
                // Changes are against the version that was in use when this one was proposed.
                const prev = data.history.slice(i + 1).find(h => h.status === "APPROVED");
                const diff = prev ? changedKeys(prev.config, v.config) : [];
                const badge = COMP_STATUS_BADGE[v.status];
                return (
                  <TableRow key={v.id} className="align-top">
                    <TableCell className="px-3 font-semibold">v{v.version}{v.version === data.active.version && <span className="ml-1 text-[11px] font-normal text-[var(--color-success-fg)]">in use</span>}</TableCell>
                    <TableCell className="px-3">{badge && <Badge tone={badge.tone}>{v.status === "APPROVED" ? "Approved" : badge.label}</Badge>}</TableCell>
                    <TableCell className="px-3 text-[12px]">{name(v.submittedBy)}<div className="text-surface-400">{fmtDate(v.submittedAt)}</div></TableCell>
                    <TableCell className="px-3 text-[12px]">
                      {v.decidedAt ? <>{name(v.decidedBy)}<div className="text-surface-400">{fmtDate(v.decidedAt)}</div></> : "—"}
                    </TableCell>
                    <TableCell className="px-3 text-[12px]">
                      {prev ? (
                        diff.length ? (
                          <ul className="m-0 list-none p-0">
                            {diff.map(k => <li key={k}>{META[k].label}: {show(k, prev.config[k])} → <span className="font-semibold">{show(k, v.config[k])}</span></li>)}
                          </ul>
                        ) : <span className="text-surface-400">No value changes</span>
                      ) : <span className="text-surface-400">Initial rules</span>}
                      {v.note && <div className="mt-1 text-surface-500">“{v.note}”</div>}
                      {v.applySummary ? (
                        <div className="mt-1 text-surface-600">
                          Applied to all from {fmtDate(v.applySummary.appliesFrom)}: {v.applySummary.restructured} restructured,{" "}
                          {v.applySummary.recalculated} open recalculated, {v.applySummary.unchanged} unchanged, {v.applySummary.skipped.length} skipped
                          {v.applySummary.skipped.length > 0 && (
                            <ul className="m-0 mt-0.5 list-disc pl-4 text-surface-500">
                              {v.applySummary.skipped.map(s => <li key={s.employeeId}>{s.name} — {s.reason}</li>)}
                            </ul>
                          )}
                        </div>
                      ) : v.appliesFrom && v.status === "PENDING_VERIFICATION" ? (
                        <div className="mt-1 text-surface-500">Applies to all from {fmtDate(v.appliesFrom)} once approved</div>
                      ) : null}
                      {v.decisionComment && <div className="mt-1 text-surface-500">Finance: “{v.decisionComment}”</div>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>}
      </Card>}
    </div>
  );
}

// ── Live preview: try a CTC against the rules in use (and the unsent changes) ──

interface PreviewLine { componentKey: string; annualAmount: number; monthlyAmount: number }

function StructurePreview({ active, draft, draftLabel = "Your changes" }: {
  active: StructureConfig; draft: StructureConfig | null;
  /** Column heading for `draft`: "Your changes" while editing, "Proposed" in Finance's view. */
  draftLabel?: string;
}) {
  const [ctc, setCtc] = useState("600000");
  const [rows, setRows] = useState<{ now: PreviewLine[]; next: PreviewLine[] | null } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false); // the pop-up; nothing is calculated until it opens
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!open) return;
    const ctcNum = Number(ctc);
    if (!ctcNum || ctcNum <= 0) { setRows(null); return; }
    timer.current = setTimeout(async () => {
      const body = (structure: StructureConfig) => ({
        ctcAnnual: ctcNum, bonusAnnual: 0, taxRegime: "NEW_REGIME", professionalTaxMonthly: 0,
        includeMedicalInsurance: false, medicalInsuranceMonthly: 0, structure,
      });
      try {
        const [now, next] = await Promise.all([
          api.post<{ lines: PreviewLine[] }>("/compensation/compute-preview", body(active)),
          draft ? api.post<{ lines: PreviewLine[] }>("/compensation/compute-preview", body(draft)) : Promise.resolve(null),
        ]);
        setRows({ now: now.data.lines, next: next?.data.lines ?? null }); setErr(null);
      } catch (e) {
        logError("CompStructurePanel.preview", e);
        setErr(errMsg(e) ?? "Couldn't calculate the preview.");
      }
    }, 500);
  }, [open, ctc, active, draft]);

  const amt = (lines: PreviewLine[], k: string) => lines.find(l => l.componentKey === k)?.annualAmount ?? 0;
  const keys = rows ? EARN_KEYS.filter(k => k !== "BONUS" && (amt(rows.now, k) || (rows.next && amt(rows.next, k)))) : [];
  const deductionKeys = ["PF_EMPLOYEE", "PF_EMPLOYER", "GRATUITY", "ESI_EMPLOYEE", "ESI_EMPLOYER"]
    .filter(k => rows && (amt(rows.now, k) || (rows.next && amt(rows.next, k))));
  const cell = (lines: PreviewLine[], k: string) => `₹${fmtAmt(amt(lines, k))}`;
  const monthly = (lines: PreviewLine[], k: string) => `₹${fmtAmt(lines.find(l => l.componentKey === k)?.monthlyAmount ?? 0)}`;

  return (
    <>
    <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => setOpen(true)}>
      <Calculator className="h-3.5 w-3.5" /> Try a CTC
    </Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[85vh] w-[95vw] max-w-[95vw] content-start overflow-y-auto sm:w-190 sm:max-w-190">
        <DialogHeader>
          <DialogTitle>Try a CTC</DialogTitle>
          <DialogDescription>
            See the yearly and monthly breakdown for any CTC{draft ? ` — under the rules in use and ${draftLabel.toLowerCase()} side by side` : " under the rules in use"}. Nothing is saved.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-surface-500">CTC ₹/yr</span>
            <Input type="number" min={0} step={50000} className="h-8 w-40 text-right tabular-nums" value={ctc} onChange={e => setCtc(e.target.value)} autoFocus />
          </label>
        </div>
        {err && <p className="m-0 text-[12px] text-[var(--color-error-fg)]">{err}</p>}
        {rows && (
          <div className="overflow-x-auto rounded-md border border-surface-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Component</TableHead>
                  <TableHead className="text-right">{rows.next ? "In use · yearly" : "Yearly"}</TableHead>
                  <TableHead className="text-right">{rows.next ? "In use · monthly" : "Monthly"}</TableHead>
                  {rows.next && <TableHead className="text-right">{draftLabel} · yearly</TableHead>}
                  {rows.next && <TableHead className="text-right">{draftLabel} · monthly</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...keys, ...deductionKeys].map(k => {
                  const diff = rows.next && Math.round(amt(rows.next, k) - amt(rows.now, k)) !== 0;
                  const changed = cn("px-3 text-right tabular-nums", diff && "font-semibold text-[var(--color-warning-fg-strong)]");
                  return (
                    <TableRow key={k}>
                      <TableCell className="px-3">{COMP_LABELS[k] ?? k}</TableCell>
                      <TableCell className="px-3 text-right tabular-nums">{cell(rows.now, k)}</TableCell>
                      <TableCell className="px-3 text-right tabular-nums text-surface-500">{monthly(rows.now, k)}</TableCell>
                      {rows.next && <TableCell className={changed}>{cell(rows.next, k)}</TableCell>}
                      {rows.next && <TableCell className={changed}>{monthly(rows.next, k)}</TableCell>}
                    </TableRow>
                  );
                })}
                <TableRow className="font-semibold">
                  <TableCell className="px-3">Take home</TableCell>
                  <TableCell className="px-3 text-right tabular-nums">₹{fmtAmt(takeHomeBeforeTax(rows.now))}</TableCell>
                  <TableCell className="px-3 text-right tabular-nums">₹{fmtAmt(takeHomeBeforeTax(rows.now) / 12)}</TableCell>
                  {rows.next && <TableCell className="px-3 text-right tabular-nums">₹{fmtAmt(takeHomeBeforeTax(rows.next))}</TableCell>}
                  {rows.next && <TableCell className="px-3 text-right tabular-nums">₹{fmtAmt(takeHomeBeforeTax(rows.next) / 12)}</TableCell>}
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
